import {
	addToast,
	Alert,
	AlertProps,
	Button,
	Card,
	CardBody,
	CardHeader,
	Chip,
	Divider,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppApi } from "src/appApi";
import { TRequest } from "@shared/entities";
import { useLocation, useParams } from "wouter";
import { Icon } from "@iconify/react";
import { useStore } from "src/store/store";
import { useAdminStore } from "src/store/adminStore";
import { useModal } from "src/infra/ModalProvider/ModalContext";
import { DoctorRejectRequestModal } from "src/components/modals/DoctorRejectRequestModal";
import { DoctorApproveRequestModal } from "src/components/modals/DoctorApproveRequestModal";
import { AdminApproveRequestModal } from "src/components/modals/AdminApproveRequestModal";
import { AdminBackRequestToDoctorModal } from "src/components/modals/AdminBackRequestToDoctorModal";
import { AdminRequestExternalDocumentsModal } from "src/components/modals/AdminRequestExternalDocumentsModal";
import { AdminReturnRequestToBrokerModal } from "src/components/modals/AdminReturnRequestToBrokerModal";
import { AdminRejectRequestModal } from "src/components/modals/AdminRejectRequestModal";
import { AdminRequestPaymentModal } from "src/components/modals/AdminRequestPaymentModal";
import { ServiceAndPaymentCard } from "./ServiceAndPaymentCard";
import { DeclarationSummary } from "./DeclarationSummary";
import { FileUtils } from "src/utils/FileUtils";
import { AttachDoctorModal } from "src/components/modals/AttachDoctorModal";
import { RequestDetails } from "src/components/widgets/RequestDetails/RequestDetails";
import { TransportersList } from "./TransportersList";
import { MedicalQuestions } from "./MedicalQuestions";
import { TRequestActivity } from "@shared/entities/requestActivity";
import { ActivityTimeline } from "./ActivityTimeline";
import { useConfirm } from "src/hooks/useConfirm";
import { UseTemplateModal } from "src/components/modals/UseTemplateModal/UseTemplateModal";

export default function AdminRequestDetailsPage() {
	const appApi = useAppApi();
	const adminStore = useAdminStore();
	const { user } = useStore();

	const params = useParams();

	const [activity, setActivity] = useState<TRequestActivity[]>([]);

	const { t } = useTranslation(["requestDetailsPage", "common"]);

	const requestId = params.id;

	const [request, setRequest] = useState<TRequest | null>(null);

	useEffect(() => {
		if (!requestId) return;

		appApi.api.admin.getRequest(requestId).then((res) => {
			setRequest(res.data);

			// Admin-only: the log records admin decisions and payments.
			if (user?.admin) {
				appApi.api.admin.getRequestActivity(requestId).then((res) => {
					setActivity(res.data ?? []);
				});
			}
		});
	}, []);

	// fetch doctors
	useEffect(() => {
		appApi.api.admin.getDoctors();
	}, []);

	if (!request) return null;

	// "default" | "primary" | "secondary" | "success" | "warning" | "danger"
	const requestStatus: Record<TRequest["status"], string> = {
		draft: "הלקוח לא סיים את הבקשה או התשלום",
		pending: "ממתין לאישור הבקשה על ידי אדמין ושיוך לרופא",
		pending_doctor: "בקשמה נמצאת בטיפול אצל הרופא",
		approved_by_doctor: "הרופא אישר את הבקשה",
		rejected_by_doctor: "הרופא דחה את הבקשה",
		pending_external_documents: "ממתין למסמכים חיצוניים",
		change_required: "לקוח צריך להשלים מסמכים או מידע חסר",
		change_required_broker: "מתווך צריך להשלים מסמכים או מידע חסר",
		completed: "הבקשה הסתיימה בהצלחה",
		rejected: "הבקשה הסתיימה בדחיה",
	};

	const requestStatusColor: Record<TRequest["status"], AlertProps["color"]> = {
		draft: "default",
		pending: "primary",
		pending_doctor: "secondary",
		approved_by_doctor: "secondary",
		rejected_by_doctor: "secondary",
		pending_external_documents: "warning",
		change_required: "warning",
		change_required_broker: "warning",
		completed: "success",
		rejected: "danger",
	};

	return (
		<main className="min-h-screen container w-full text-start p-2 sm:p-6">
			<Alert
				color={requestStatusColor[request.status]}
				title={requestStatus[request.status]}
				className="my-4"
			/>
			<RequestDetails request={request}>
				<RequestDetails.Card>
					<RequestDetails.CardHeader />
					<RequestDetails.CardBody>
						<RequestDetails.RecommendationOnlyAlert />
						{user?.admin && <ServiceAndPaymentCard request={request} />}
						{user?.admin && <DeclarationSummary request={request} />}
						<Divider />
						<RequestDetails.ClientDetails />

						{request.reasonType && (
							<Card>
								<CardHeader>
									<h3 className="text-lg font-semibold">אינדיקציה רפואית</h3>
								</CardHeader>
								<CardBody>
									<div className="flex items-center gap-3">
										<Icon icon="lucide:heart" className="text-primary" />
										<span className="text-lg">
											{t(`reasonType.${request.reasonType}` as any)}
										</span>
									</div>
								</CardBody>
							</Card>
						)}
						<Divider />
						<DoctorInfo request={request} doctors={adminStore.doctors} />
						<Divider />
						<RequestDetails.DosageCard />
						<Divider />
						{!!request.userComment && (
							<Alert variant="solid" color="warning">
								{request.userComment}
							</Alert>
						)}
						<RequestDetails.ApproveMessageByDoctor />
						<RequestDetails.AdminMessage />
						<RequestDetails.ClientDocuments />
						<Divider />
						<RequestDetails.ClientDocumentsForClient />
						{request.documentsForClient && <Divider />}
						<RequestDetails.DoctorDocuments />
						<Divider />
						{/* Transporters */}
						{request.medicalQuestionnaire.hasTransporters && !!request.transporters && (
							<Card>
								<CardHeader>
									<h2 className="text-xl font-semibold">משנעים</h2>
								</CardHeader>
								<Divider />
								<CardBody>
									<TransportersList transporters={request.transporters} />
								</CardBody>
							</Card>
						)}
						<div className="grid grid-cols-1  gap-6">
							<Card className="h-full">
								<CardHeader>
									<h2 className="text-xl font-semibold">שאלון רפואי</h2>
								</CardHeader>
								<Divider />
								<CardBody>
									<MedicalQuestions questionnaire={request.medicalQuestionnaire} />
								</CardBody>
							</Card>
						</div>
						<Divider />
						{user?.admin && activity && activity.length > 0 && (
							<Card>
								<CardHeader className="flex justify-between items-center">
									<h2 className="text-xl font-semibold">היסטוריית פעילות</h2>
									<Chip size="sm" color="primary" variant="flat">
										{activity.length}
									</Chip>
								</CardHeader>
								<Divider />
								<CardBody className="overflow-x-hidden">
									<ActivityTimeline activity={activity} />
								</CardBody>
							</Card>
						)}
						<Divider />
						<ActionButtons setRequest={setRequest} request={request} />
					</RequestDetails.CardBody>
				</RequestDetails.Card>
			</RequestDetails>
		</main>
	);
}

interface DocumentListProps {
	documents: TRequest["documents"];
}

interface ActionButtonsProps {
	request: TRequest;
	setRequest: (request: TRequest) => void;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({ request, setRequest }) => {
	const modalApi = useModal();

	const { user } = useStore();
	const appApi = useAppApi();

	const [, navigate] = useLocation();

	const { t } = useTranslation(["requestDetailsPage", "common", "admin", "doctorTemplates"]);

	const handleBackToDoctor = () => {
		// startTransition(() => {
		const id = modalApi.open(() => {
			return (
				<AdminBackRequestToDoctorModal
					onSubmit={(newRequest) => {
						setRequest({ ...request, ...newRequest } as TRequest);

						modalApi.close(id);
					}}
					request={request}
				/>
			);
		});
		// });
	};

	const handleRequestExternalDocuments = () => {
		const id = modalApi.open(() => {
			return (
				<AdminRequestExternalDocumentsModal
					onSubmit={(newRequest) => {
						setRequest({ ...request, ...newRequest } as TRequest);
						modalApi.close(id);
					}}
					request={request}
				/>
			);
		});
	};

	const handleReturnToBroker = () => {
		const id = modalApi.open(() => {
			return (
				<AdminReturnRequestToBrokerModal
					onSubmit={(newRequest) => {
						setRequest({ ...request, ...newRequest } as TRequest);
						modalApi.close(id);
					}}
					request={request}
				/>
			);
		});
	};

	const handleRequestPayment = () => {
		const id = modalApi.open(() => {
			return (
				<AdminRequestPaymentModal
					onSubmit={() => {
						modalApi.close(id);
					}}
					request={request}
				/>
			);
		});
	};

	const handleApprove = async () => {
		// Read the request fresh: this page holds the copy it loaded, and the
		// dialog must start from what is actually saved — a stale empty slot
		// would otherwise look like "no document" to the admin.
		// If the read fails, the page copy is still safe to start from: approval
		// writes only the types the admin touched, so a stale empty slot can
		// never overwrite a saved file.
		let fresh: TRequest = request;
		try {
			fresh = (await appApi.api.admin.getRequest(request.id)).data ?? request;
			setRequest(fresh);
		} catch (error) {
			console.error("[approve] could not re-read the request; using the loaded copy", error);
		}
		// The dialog closes itself after a save (see AdminApproveRequestModal).
		modalApi.open(() => {
			return (
				<AdminApproveRequestModal
					request={fresh}
					onSubmit={async (documentsForClient) => {
						const res = await appApi.api.admin.approveRequest({
							id: fresh.id,
							userId: fresh.userId,
							documentsForClient,
							savedDocumentsForClient: fresh.documentsForClient ?? {},
						});
						if (!res.success) {
							addToast({
								title: "אישור הבקשה נכשל",
								description: "שום דבר לא נשמר — נסו לאשר שוב.",
								color: "danger",
							});
							return false;
						}
						// Saved. The dialog closes itself once this returns true; the page
						// refresh runs in the background so nothing here can keep it open.
						void appApi.api.admin
							.getRequest(fresh.id)
							.then((saved) => saved.data && setRequest(saved.data))
							.catch((error) => console.error("[approve] approved; refreshing the page failed", error));
						if (res.profileCopy === "failed" || res.notCopied.length > 0) {
							addToast({
								title:
									res.profileCopy === "failed"
										? "הבקשה אושרה, אבל המסמכים לא נשמרו בפרופיל הלקוח"
										: "הבקשה אושרה, אבל חלק מהמסמכים לא נשמרו בפרופיל הלקוח (פורמט קובץ לא נתמך)",
								description: "הלקוח רואה אותם בבקשה. אפשר להעלות אותם לפרופיל ידנית.",
								color: "warning",
							});
							return true;
						}
						addToast({
							title: "הבקשה אושרה",
							description:
								res.profileCopy === "copied"
									? "המסמכים זמינים ללקוח ונשמרו גם בפרופיל שלו."
									: res.profileCopy === "no-profile"
										? "המסמכים זמינים ללקוח בבקשה."
										: undefined,
							color: "success",
						});
						return true;
					}}
				/>
			);
		});
	};
	const handleReject = () => {
		const id = modalApi.open(() => {
			return (
				<AdminRejectRequestModal
					request={request}
					onSubmit={async (data) => {
						const res = await appApi.api.admin.rejectRequest({
							id: request.id,
							adminMessageForClient: data.adminMessageForClient,
						});
						if (res.success) {
							setRequest({ ...request, ...res.data });
						}
						modalApi.close(id);
					}}
					onClose={() => modalApi.close(id)}
				/>
			);
		});
	};

	const confirmDelete = useConfirm({
		title: t("admin:confirmations.deleteRequest.title"),
		message: t("admin:confirmations.deleteRequest.message"),
		confirmText: t("admin:confirmations.deleteRequest.confirmText"),
		cancelText: t("admin:confirmations.deleteRequest.cancelText"),
		onConfirm: async (close) => {
			// Delete the request document - Firebase Storage cleanup will be handled by Cloud Function
			const res = await appApi.api.admin.deleteRequest({ id: request.id });
			if (res.success) {
				close();
				navigate("/admin");
			}
		},
	});

	const handleDelete = () => {
		confirmDelete();
	};

	const handleUseTemplate = () => {
		const id = modalApi.open(() => (
			<UseTemplateModal
				requestId={request.id}
				request={request}
				onSuccess={() => {
					modalApi.close(id);
					appApi.api.admin.getRequest(request.id).then((res) => {
						setRequest(res.data);
					});
				}}
				onClose={() => modalApi.close(id)}
			/>
		));
	};

	const adminActions = [
		<Button
			color="secondary"
			variant="bordered"
			onPress={() => appApi.navigate(`/admin/requests/${request.id}/edit`)}
			startContent={<Icon icon="lucide:edit" />}
		>
			{t("admin:requests.edit")}
		</Button>,
		<Button
			color="primary"
			variant="flat"
			onPress={handleRequestPayment}
			startContent={<Icon icon="lucide:shekel-sign" />}
		>
			בקש תשלום
		</Button>,
	];
	if (request.status !== "completed") {
		adminActions.push(
			<Button
				color="danger"
				variant="flat"
				onPress={handleReject}
				startContent={<Icon icon="lucide:x" />}
			>
				{t("admin:requests.reject")}
			</Button>,
			<Button
				variant="bordered"
				isDisabled={request.status === "draft"}
				onPress={() => {
					const id = modalApi.open(() => {
						return (
							<AttachDoctorModal
								onSave={(newRequest) => {
									if (newRequest) {
										// todo
										setRequest({ ...request, ...newRequest } as TRequest);
									}

									modalApi.close(id);
								}}
								request={request}
							/>
						);
					});
				}}
				color="secondary"
			>
				{t("admin:requests.attachDoctor")}
			</Button>
		);
	}

	// Add delete button for all requests
	adminActions.push(
		<Button
			color="danger"
			variant="solid"
			onPress={handleDelete}
			startContent={<Icon icon="lucide:trash-2" />}
		>
			{t("admin:requests.delete")}
		</Button>
	);
	if (request.status === "approved_by_doctor" || request.status === "pending_external_documents") {
		adminActions.push(
			<Button
				color="success"
				onPress={handleApprove}
				startContent={<Icon icon="lucide:check" />}
			>
				{t("admin:requests.approve")}
			</Button>
		);
	}
	if (
		request.status === "approved_by_doctor" ||
		request.status === "rejected_by_doctor" ||
		request.status === "pending_external_documents"
	) {
		adminActions.push(
			<Button
				color="secondary"
				onPress={handleBackToDoctor}
				startContent={<Icon icon="lucide:check" />}
			>
				{t("admin:requests.returnToDoctor")}
			</Button>
		);
	}
	if (
		request.status !== "completed" &&
		request.status !== "rejected" &&
		request.status !== "pending_external_documents"
	) {
		adminActions.push(
			<Button
				color="warning"
				variant="flat"
				onPress={handleRequestExternalDocuments}
				startContent={<Icon icon="lucide:file-text" />}
			>
				{t("admin:requests.requestExternalDocuments")}
			</Button>
		);
	}
	// Show return to broker button only if request was created by broker
	if (
		request.createdBy === "broker" &&
		request.status !== "completed" &&
		request.status !== "rejected" &&
		request.status !== "change_required_broker"
	) {
		adminActions.push(
			<Button
				color="warning"
				variant="flat"
				onPress={handleReturnToBroker}
				startContent={<Icon icon="lucide:arrow-left" />}
			>
				החזר למתווך
			</Button>
		);
	}
	const doctorActions =
		request.status === "pending_doctor"
			? [
					<Button
						color="danger"
						variant="flat"
						onPress={() => {
							const id = modalApi.open(() => {
								return (
									<DoctorRejectRequestModal
										onSubmit={async (form) => {
											console.log("reject", form);

											const res = await appApi.api.doctor.rejectRequest({
												message: form.rejectMessageByDoctor ?? "",
												id: request.id,
											});
											// todo
											setRequest({ ...request, ...res.data } as TRequest);
											modalApi.close(id);
										}}
									/>
								);
							});
						}}
						startContent={<Icon icon="lucide:x" />}
					>
						{t("common:request.reject")}
					</Button>,
					<Button
						color="success"
						onPress={() => {
							const id = modalApi.open(() => {
								return (
									<DoctorApproveRequestModal
										existingDocuments={request.doctorDocuments ?? []}
										onSubmit={async (data) => {
											await appApi.api.doctor.approveRequest({
												files: data.files,
												approveMessageByDoctor: data.approveMessageByDoctor,
												id: request.id,
											});
											modalApi.close(id);
											// Re-read rather than merge what was written: the write appends
											// with arrayUnion, so its echo is a sentinel, not the document's
											// real list — merging it would hide the template recommendation
											// (or crash the document list, which maps over it).
											const fresh = await appApi.api.admin.getRequest(request.id);
											if (fresh.data) setRequest(fresh.data);
										}}
									/>
								);
							});
						}}
						startContent={<Icon icon="lucide:check" />}
					>
						{t("common:request.approve")}
					</Button>,
					...(request.doctor?.id === user?.uid
						? [
								<Button
									color="primary"
									variant="flat"
									onPress={handleUseTemplate}
									startContent={<Icon icon="lucide:file-plus" />}
								>
									{t("doctorTemplates:useTemplate")}
								</Button>,
						  ]
						: []),
			  ]
			: [];

	return (
		<div dir="" className="flex flex-col sm:flex-row gap-2 justify-end">
			{user?.admin ? adminActions : user?.recommendingDoctor ? doctorActions : null}
			<Button
				onPress={() => {
					appApi.goBack(user?.admin ? "/admin/requests" : "/doctor");
				}}
			>
				{t("admin:requests.back")}
			</Button>
		</div>
	);
};

interface DoctorInfoProps {
	request: TRequest;
	doctors: any[];
}

const DoctorInfo: React.FC<DoctorInfoProps> = ({ request, doctors }) => {
	if (!request.doctor?.id) {
		return (
			<Card>
				<CardBody>
					<div className="flex items-center gap-2">
						<Icon icon="lucide:user-x" className="text-default-500" />
						<span className="text-default-500">לא שויך רופא</span>
					</div>
				</CardBody>
			</Card>
		);
	}

	const doctor = doctors.find((d) => d.id === request.doctor?.id);

	return (
		<Card>
			<CardHeader>
				<h3 className="text-lg font-semibold">רופא מטפל</h3>
			</CardHeader>
			<CardBody className="text-start">
				<div className="flex items-center gap-3">
					<Icon icon="lucide:user-md" className="text-primary" />
					<div>
						<p className="font-medium">
							{doctor
								? `${doctor.firstName} ${doctor.lastName}`
								: `רופא ID: ${request.doctor.id}`}
						</p>
						{doctor && <p className="text-small text-default-500">{doctor.email}</p>}
					</div>
				</div>
			</CardBody>
		</Card>
	);
};

export const DocumentList: React.FC<DocumentListProps> = ({ documents }) => {
	const getFileIcon = (type: string) => {
		switch (type) {
			case "pdf":
				return "lucide:file-text";
			case "doc":
			case "docx":
				return "lucide:file-type";
			default:
				return "lucide:file";
		}
	};

	const { DOCTOR_RECOMMENDATION, ID, MEDICAL_SUMMARY, CUSTOM1 } = documents ?? {};

	const docs = [DOCTOR_RECOMMENDATION, ID, MEDICAL_SUMMARY, CUSTOM1];

	// if(documents.ne)

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">Attached Documents</h3>
			<div className="space-y-2">
				{docs.map((doc: any) => {
					if (!doc) return null;
					return (
						<div
							key={doc?.url || doc?.type}
							className="flex items-center justify-between p-3 rounded-lg bg-content3"
						>
							<div className="flex items-center gap-3">
								<Icon icon={getFileIcon(doc?.type ?? "")} className="text-default-500" />
								<span>{doc?.type}</span>
							</div>
							<div className="flex gap-2">
								<Button
									size="sm"
									variant="flat"
									onPress={() => FileUtils.handlePreview({ url: doc.url })}
									startContent={<Icon icon="lucide:eye" />}
								>
									הצגה
								</Button>
								<Button
									size="sm"
									variant="flat"
									startContent={<Icon icon="lucide:download" />}
									onPress={() => {
										if (!doc) return;
										FileUtils.handleDownload({
											url: doc.url,
											name: doc.name,
										});
									}}
								>
									הורדה
								</Button>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
